import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { AlertCircle, FileImage, FileText, Trash2, UploadCloud } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface MediaItem {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  createdAt: string;
}

interface AssetListResponse {
  data: {
    items: MediaItem[];
    total: number;
  };
}

const API_URL = import.meta.env.VITE_API_URL ?? "";
const PAGE_SIZE = 24;

export default function MediaLibraryPage() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [clipboardMessage, setClipboardMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const requestSequenceRef = useRef(0);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);

  const loadAssets = useCallback(async (search: string, nextOffset: number, signal?: AbortSignal) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    if (hasLoadedOnce) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const query = search.trim();
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(nextOffset),
      });
      if (query.length > 0) {
        params.set("search", query);
      }
      const path = `/crm/pages/assets?${params.toString()}`;
      const response = await apiFetch<AssetListResponse>(path, { signal });

      if (requestSequence !== requestSequenceRef.current) {
        return;
      }

      setItems(response.data.items ?? []);
      setTotalCount(response.data.total ?? 0);
      setSelectedAssetId((current) => {
        if (current && response.data.items?.some((item) => item.id === current)) {
          return current;
        }
        return response.data.items?.[0]?.id ?? null;
      });
      setError(null);
      setHasLoadedOnce(true);
    } catch (loadError) {
      if (signal?.aborted || requestSequence !== requestSequenceRef.current) {
        return;
      }

      setError(loadError instanceof Error ? loadError.message : "Failed to load media assets.");
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [hasLoadedOnce]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    const controller = new AbortController();

    void loadAssets(debouncedSearchQuery, offset, controller.signal);

    return () => {
      controller.abort();
    };
  }, [debouncedSearchQuery, loadAssets, offset]);

  const uploadFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploads = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(`${API_URL}/crm/pages/assets`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({ error: "Upload failed" }));
          throw new Error(
            (body as { message?: string; error?: string }).message
            ?? (body as { error?: string }).error
            ?? "Upload failed",
          );
        }
      });

      const results = await Promise.allSettled(uploads);
      const failedUploads = results.filter((result) => result.status === "rejected");

      await loadAssets(debouncedSearchQuery, offset);

      if (failedUploads.length > 0) {
        const firstFailure = failedUploads[0];
        const message = firstFailure?.status === "rejected" && firstFailure.reason instanceof Error
          ? firstFailure.reason.message
          : "Upload failed.";

        throw new Error(
          failedUploads.length === results.length
            ? message
            : `${failedUploads.length} of ${results.length} uploads failed. ${message}`,
        );
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Upload failed.");
    } finally {
      setIsUploading(false);
    }
  }, [debouncedSearchQuery, loadAssets, offset]);

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    await uploadFiles(event.dataTransfer.files);
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    await uploadFiles(event.target.files);
    event.target.value = "";
  };

  const handleDelete = useCallback(async (assetId: string) => {
    setDeletingId(assetId);
    setError(null);

    try {
      await apiFetch(`/crm/pages/assets/${assetId}`, { method: "DELETE" });
      const nextTotal = Math.max(0, totalCount - 1);
      const nextOffset = nextTotal > 0 && offset >= nextTotal
        ? Math.max(0, offset - PAGE_SIZE)
        : offset;
      setTotalCount(nextTotal);
      if (nextOffset !== offset) {
        setOffset(nextOffset);
      } else {
        setItems((current) => current.filter((item) => item.id !== assetId));
        setSelectedAssetId((current) => (current === assetId ? null : current));
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  }, [offset, totalCount]);

  const isSearchActive = debouncedSearchQuery.trim().length > 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const selectedAsset = useMemo(
    () => items.find((item) => item.id === selectedAssetId) ?? null,
    [items, selectedAssetId],
  );
  const selectedAssetUrl = selectedAsset ? buildAssetAbsoluteUrl(selectedAsset.id) : "";

  const assetCountLabel = useMemo(() => {
    if (isLoading) {
      return "Loading media library";
    }

    if (isSearchActive) {
      return `${totalCount} matching asset${totalCount === 1 ? "" : "s"}`;
    }

    return `${totalCount} asset${totalCount === 1 ? "" : "s"} stored`;
  }, [isLoading, isSearchActive, totalCount]);

  const handleCopyUrl = useCallback(async () => {
    if (!selectedAssetUrl || typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      setError("Clipboard access is not available in this browser.");
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedAssetUrl);
      setClipboardMessage("Asset URL copied.");
      setTimeout(() => setClipboardMessage(null), 2000);
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : "Failed to copy asset URL.");
    }
  }, [selectedAssetUrl]);

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.12),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">Media Library</h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              Upload, search, and remove the real assets stored for your page-builder tenant. This surface manages the mounted
              <code> /crm/pages/assets </code>
              contract only and reflects the current backend truth instead of a placeholder gallery.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 lg:items-end">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
              {assetCountLabel}
              {isRefreshing ? <Skeleton className="h-4 w-4 bg-muted-foreground/20" /> : null}
            </span>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Skeleton className="h-4 w-4 bg-white/50" />
                  <Skeleton className="h-4 w-16 bg-white/50" />
                </>
              ) : (
                <>
                  <UploadCloud className="h-4 w-4" />
                  Upload files
                </>
              )}
            </button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,font/woff,font/woff2,font/ttf,font/otf"
          onChange={(event) => { void handleFileSelect(event); }}
          className="hidden"
        />
      </div>

      {error ? (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="space-y-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search stored files..."
          className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <p className="text-xs text-muted-foreground">
          Search runs against the mounted asset backend by filename. Upload validation remains enforced server-side.
        </p>
      </div>

      <div
        onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => { void handleDrop(event); }}
        className={`rounded-[24px] border-2 border-dashed p-8 transition-colors ${
          isDragging ? "border-primary bg-primary/5" : "border-border bg-card"
        }`}
      >
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={index} className="h-52 rounded-2xl bg-background" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center">
            <FileImage className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 font-medium text-foreground">
              {isDragging
                ? "Drop files to upload them now"
                : isSearchActive
                  ? "No assets match this search"
                  : "No stored assets yet"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {isSearchActive
                ? "Try a different filename or clear the search to see the full tenant asset library."
                : "Upload images, PDFs, and brand fonts here. Existing backend validation will reject unsupported or unsafe files."}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)]">
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                {items.map((item) => {
              const isImage = item.contentType.startsWith("image/");
              const isDeleting = deletingId === item.id;
              const isSelected = item.id === selectedAssetId;

              return (
                <article
                  key={item.id}
                  className={`stagger-item rounded-2xl border bg-background p-4 shadow-sm transition-colors ${
                    isSelected ? "border-primary ring-1 ring-primary/30" : "border-border"
                  }`}
                  onClick={() => setSelectedAssetId(item.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                        {isImage ? <FileImage className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{item.filename}</p>
                        <p className="text-xs text-muted-foreground">{item.contentType}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDelete(item.id);
                      }}
                      disabled={isDeleting}
                      className="rounded-lg border border-border p-2 text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-50"
                      aria-label={`Delete ${item.filename}`}
                    >
                      {isDeleting ? <Skeleton className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>

                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Size</dt>
                      <dd className="mt-1 font-medium text-foreground">{formatSize(item.sizeBytes)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Uploaded</dt>
                      <dd className="mt-1 font-medium text-foreground">{formatDate(item.createdAt)}</dd>
                    </div>
                    {item.width && item.height ? (
                      <div className="sm:col-span-2">
                        <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Dimensions</dt>
                        <dd className="mt-1 font-medium text-foreground">{item.width} × {item.height}</dd>
                      </div>
                    ) : null}
                  </dl>

                  <div className="mt-4 rounded-xl border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    Asset record stored. Reuse this file from the mounted builder asset inventory when attaching media to page components.
                  </div>
                </article>
              );
                })}
              </div>

              {totalCount > PAGE_SIZE ? (
                <div className="flex items-center justify-between rounded-2xl border border-border bg-background px-4 py-3 text-sm">
                  <span className="text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
                      disabled={offset === 0 || isRefreshing}
                      className="rounded-lg border border-border px-3 py-1.5 text-foreground disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => setOffset((current) => current + PAGE_SIZE)}
                      disabled={offset + PAGE_SIZE >= totalCount || isRefreshing}
                      className="rounded-lg border border-border px-3 py-1.5 text-foreground disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <aside className="rounded-2xl border border-border bg-background p-5 shadow-sm">
              {selectedAsset ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Asset Detail</p>
                    <h2 className="mt-2 text-lg font-semibold text-foreground">{selectedAsset.filename}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{selectedAsset.contentType}</p>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-border bg-muted/30">
                    {selectedAsset.contentType.startsWith("image/") ? (
                      <img
                        src={selectedAssetUrl}
                        alt={selectedAsset.filename}
                        className="h-56 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-56 items-center justify-center text-center text-sm text-muted-foreground">
                        Preview is not available for this file type.
                      </div>
                    )}
                  </div>

                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <DetailItem label="Size" value={formatSize(selectedAsset.sizeBytes)} />
                    <DetailItem label="Uploaded" value={formatDate(selectedAsset.createdAt)} />
                    <DetailItem
                      label="Dimensions"
                      value={selectedAsset.width && selectedAsset.height ? `${selectedAsset.width} × ${selectedAsset.height}` : "—"}
                    />
                    <DetailItem label="Library URL" value={selectedAssetUrl} mono />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => { void handleCopyUrl(); }}
                      className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
                    >
                      Copy URL
                    </button>
                    <a
                      href={selectedAssetUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border border-border px-3 py-2 text-sm text-foreground"
                    >
                      Open asset
                    </a>
                  </div>

                  {clipboardMessage ? (
                    <p className="text-sm text-muted-foreground">{clipboardMessage}</p>
                  ) : null}
                </div>
              ) : (
                <div className="flex h-full min-h-72 items-center justify-center text-center text-sm text-muted-foreground">
                  Select an asset to inspect its metadata and copy the mounted file URL.
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</dt>
      <dd className={`mt-1 break-all font-medium text-foreground ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [delayMs, value]);

  return debouncedValue;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function buildAssetFilePath(assetId: string): string {
  return `/crm/pages/assets/${assetId}/file`;
}

function buildAssetAbsoluteUrl(assetId: string): string {
  if (typeof window === "undefined") {
    return buildAssetFilePath(assetId);
  }

  return `${window.location.origin}${buildAssetFilePath(assetId)}`;
}
